import { useEffect, useRef, useState } from 'react';

const SCROLL_EDGE_PX = 4;

export type HorizontalScrollFadeSide = 'left' | 'right';

/**
 * Horizontal overflow scroll: edge fade flags + vertical wheel → scrollLeft.
 * Attach `viewportRef` to the overflow-x container.
 *
 * To be used with Edge fade. For an example, see bento/IconLinkCards
 */
export function useHorizontalScroll(dependencyKey?: unknown) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const updateFades = () => {
      const maxScroll = viewport.scrollWidth - viewport.clientWidth;
      if (maxScroll <= SCROLL_EDGE_PX) {
        setShowLeftFade(false);
        setShowRightFade(false);
        return;
      }
      setShowLeftFade(viewport.scrollLeft > SCROLL_EDGE_PX);
      setShowRightFade(viewport.scrollLeft < maxScroll - SCROLL_EDGE_PX);
    };

    // map vertical wheel to horizontal scroll so shift isn't required
    const onWheel = (event: WheelEvent) => {
      if (viewport.scrollWidth <= viewport.clientWidth) return;
      if (event.deltaY === 0) return;
      // trackpads already scrolling sideways — leave native behavior
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

      event.preventDefault();
      viewport.scrollLeft += event.deltaY;
    };

    updateFades();
    viewport.addEventListener('scroll', updateFades, { passive: true });
    viewport.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('resize', updateFades);

    return () => {
      viewport.removeEventListener('scroll', updateFades);
      viewport.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', updateFades);
    };
  }, [dependencyKey]);

  const fadeSides: HorizontalScrollFadeSide[] = [
    ...(showLeftFade ? (['left'] as const) : []),
    ...(showRightFade ? (['right'] as const) : []),
  ];

  return { viewportRef, showLeftFade, showRightFade, fadeSides };
}
